import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { searchInfluencers } from "../services/influencerSearchService";
import { mapApiInfluencerToUiModel } from "../mappers/influencerMapper";

/** deterministic 32-bit hash (FNV-1a-ish) */
const hash32 = (str) => {
  let h = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const MAX_SEEN_IDS = 2000;

const toIntOrUndef = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : undefined;
};

const toFloatOrUndef = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = parseFloat(String(v));
  return Number.isFinite(n) ? n : undefined;
};

const normalizeTags = (raw) => {
  const s = String(raw || "").replaceAll("#", " ").trim();
  if (!s) return "";

  const parts = s
    .split(/[,\s]+/g)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set(parts)).join(",");
};

const getStableId = (obj) =>
  obj?.keyId ||
  obj?.external_id ||
  obj?.channelId ||
  obj?.id ||
  obj?.username ||
  null;

const buildQueryKey = (arg) => {
  const a = arg || {};
  return [
    String(a.platform || "").toLowerCase(),
    String(a.locationSlug || a.location || "").toLowerCase(),
    String(a.nicheSlug || a.niche || "").toLowerCase(),
    normalizeTags(a.tags),
    String(a.followerCount ?? ""),
    String(a.engagementRate ?? ""),
    String(a.sort || "discover").toLowerCase(),
    a.excludeSeen ? "1" : "0",
    String(a.seed || ""),
  ].join("|");
};

export const performSearchAsync = createAsyncThunk(
  "search/performSearch",
  async (
    {
      platform,
      niche,
      location,
      nicheSlug,
      locationSlug,
      followerCount,
      engagementRate,
      tags,
      tagMode = "any",
      lastKey,
      limit = 50,
      sort = "discover",
      seed = "",
      excludeSeen = true,
    },
    { rejectWithValue, signal } // ✅ no extra/getAccessToken
  ) => {
    try {
      const cityFinal = String(locationSlug || location || "").toLowerCase().trim();
      const nicheFinal = String(nicheSlug || niche || "").toLowerCase().trim();

      // ✅ apiRequest injects Authorization automatically, so no token passing
      const response = await searchInfluencers({
        platform: String(platform || "").toLowerCase(),
        city: cityFinal,
        niche: nicheFinal,
        tags: normalizeTags(tags),
        tagMode,

        minFollowers: toIntOrUndef(followerCount),
        minEngagementRate: toFloatOrUndef(engagementRate),

        sort,
        seed,
        excludeSeen,

        minMediaCount: undefined,
        onlyActive: true,
        limit,
        lastKey,
        signal,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Search request failed");
    }
  }
);

const initialState = {
  searchTerm: "",
  niche: "",
  location: "",
  nicheSlug: "",
  locationSlug: "",
  followerCount: "",
  engagementRate: "",

  sort: "discover",
  seed: "",
  excludeSeen: true,

  seenIds: [],

  results: [],
  lastKey: null,
  lastMeta: null,
  activeRequestId: null,
  activeLoadMoreRequestId: null,
  loading: false,
  loadingMore: false,
  error: null,

  confirmationMessage: "",
  isSaving: false,
  errors: {},

  hasSearched: false,
  lastQueryKey: null,
  lastSearchAt: null,
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setField: (state, action) => {
      const { field, value } = action.payload;
      state[field] = value;
    },

    clearResults: (state) => {
      state.results = [];
      state.lastKey = null;
      state.lastMeta = null;
      state.error = null;
      state.loading = false;
      state.loadingMore = false;
    },

    reset: (state) => {
      const keepSeenIds = state.seenIds;
      Object.assign(state, { ...initialState, seenIds: keepSeenIds });
    },

    clearSeenIds: (state) => {
      state.seenIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearchAsync.pending, (state, action) => {
        const req = action.meta?.arg || {};
        const isLoadMore = Boolean(req.lastKey);

        state.error = null;
        state.errors = { ...state.errors, search: undefined };

        if (isLoadMore) {
          state.loadingMore = true;
          state.activeLoadMoreRequestId = action.meta.requestId;
        } else {
          state.loading = true;
          state.activeRequestId = action.meta.requestId;
          state.hasSearched = true;
        }
      })
      .addCase(performSearchAsync.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const items = payload.items || payload.Items || [];
        const last_key = payload.last_key ?? payload.lastKey ?? null;
        const meta = payload.meta || null;

        const req = action.meta?.arg || {};
        const isLoadMore = Boolean(req.lastKey);

        if (isLoadMore) {
          if (state.activeLoadMoreRequestId !== action.meta.requestId) return;
        } else if (state.activeRequestId !== action.meta.requestId) {
          return;
        }

        state.lastMeta = meta;

        const reqSort = String(req.sort || "discover").toLowerCase();
        const reqSeed = String(req.seed || "");
        const reqExcludeSeen = Boolean(req.excludeSeen);

        let mapped = items.map(mapApiInfluencerToUiModel);

        if (!isLoadMore && reqExcludeSeen && state.seenIds.length) {
          const seen = new Set(state.seenIds);
          mapped = mapped.filter((x) => {
            const id = getStableId(x);
            return id ? !seen.has(id) : true;
          });
        }

        const backendSeedApplied = meta?.seed_applied === true;
        if (!isLoadMore && reqSort === "discover" && reqSeed && !backendSeedApplied) {
          const seedH = hash32(reqSeed);
          mapped.sort((a, b) => {
            const aId = String(getStableId(a) || "");
            const bId = String(getStableId(b) || "");
            return (hash32(aId) ^ seedH) - (hash32(bId) ^ seedH);
          });
        }

        if (isLoadMore) {
          const existing = new Set((state.results || []).map(getStableId).filter(Boolean));
          const merged = [...(state.results || [])];

          for (const r of mapped) {
            const id = getStableId(r);
            if (!id || !existing.has(id)) {
              merged.push(r);
              if (id) existing.add(id);
            }
          }
          state.results = merged;
        } else {
          state.results = mapped;
        }

        const newSeen = mapped.map(getStableId).filter(Boolean);
        if (newSeen.length) {
          const set = new Set(state.seenIds);
          for (const id of newSeen) set.add(id);
          const arr = Array.from(set);
          state.seenIds = arr.slice(Math.max(0, arr.length - MAX_SEEN_IDS));
        }

        state.lastKey = last_key;

        state.loading = false;
        state.loadingMore = false;
        if (isLoadMore) state.activeLoadMoreRequestId = null;
        else state.activeRequestId = null;

        state.lastQueryKey = buildQueryKey(req);
        state.lastSearchAt = Date.now();
      })
      .addCase(performSearchAsync.rejected, (state, action) => {
        const req = action.meta?.arg || {};
        const isLoadMore = Boolean(req.lastKey);

        if (isLoadMore) {
          if (state.activeLoadMoreRequestId !== action.meta.requestId) return;
        } else if (state.activeRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.loadingMore = false;
        if (isLoadMore) state.activeLoadMoreRequestId = null;
        else state.activeRequestId = null;

        if (action.meta?.aborted) {
          state.error = null;
          return;
        }

        if (!isLoadMore) {
          state.results = [];
          state.lastKey = null;
          state.lastMeta = null;
        }

        state.error = action.payload || "Failed to perform search.";
      });
  },
});

export const { setField, clearResults, reset, clearSeenIds } = searchSlice.actions;
export default searchSlice.reducer;
